/**
 * The agreement leaf's own failures. Neither reaches the wire as itself: the
 * handler turns a closed agreement into the envelope's 409 conflict and a
 * store failure into a defect the boundary renders as an enveloped 500 with a
 * trace id, so no driver message reaches a caller.
 */
import { Schema as S } from "effect"

/**
 * The firm cannot take an agreement now: it already has one and has not
 * failed a move, or a move is under way or done. 031's insert check raises
 * SQLSTATE 23000 for exactly this, and the service also says it before the
 * write when the state it read already shows it.
 */
export class AgreementClosedError extends S.TaggedError<AgreementClosedError>()("AgreementClosedError", {
  message: S.String,
}) {}

/** A read or the write did not complete. */
export class AgreementRepositoryError extends S.TaggedError<AgreementRepositoryError>()("AgreementRepositoryError", {
  message: S.String,
  cause: S.Unknown,
  operation: S.String,
}) {}

/**
 * The firm's sync is on and its first read of clients and contacts is not done
 * (decision 036, follow-up 3), so it cannot agree yet. The handler's 409.
 */
export class AgreementReadPendingError extends S.TaggedError<AgreementReadPendingError>()("AgreementReadPendingError", {
  message: S.String,
}) {}

/**
 * The firm's saved answers could not be read, so the agreement, which needs
 * them (every row Cova does not lock answered by a person), was not given.
 * Nothing was written. On the wire (code review xhigh, finding 1): a 503 whose
 * envelope code is the catalogue's `service_unavailable` (the catalogue is
 * frozen, spec 003 §1) and whose one detail carries the machine code
 * `answersUnreadable`, with these closed words and never a driver's text.
 * The page offers to try again.
 */
export class AgreementAnswersUnreadable extends S.TaggedError<AgreementAnswersUnreadable>()("AgreementAnswersUnreadable", {
  message: S.String,
}) {}

/** The words of `AgreementAnswersUnreadable`, which the page also reads. */
export const ANSWERS_UNREADABLE_REFUSAL = "Your saved answers couldn't be read just now. Nothing was agreed. Try again."

/** The refusal's machine code, in its one `details` entry (field `answers`). */
export const ANSWERS_UNREADABLE_CODE = "answersUnreadable"

/** The firm's reading sync connection did not pause, so the agreement was not written. The handler's 409. */
export class AgreementSyncNotPaused extends S.TaggedError<AgreementSyncNotPaused>()("AgreementSyncNotPaused", {
  message: S.String,
}) {}

/**
 * A staff amendment that cannot be recorded (Tai, 2026-11-03). `wrongState`:
 * the firm cannot take a new agreement now, or the agreement named is not its
 * latest; the handler's 409. `invalid`: a listed line, or the amended agreement
 * against today's clients, does not hold; the handler's 400 with `details`.
 * Messages are closed words; details carry ids and closed codes only.
 */
export class AgreementAmendmentRefused extends S.TaggedError<AgreementAmendmentRefused>()("AgreementAmendmentRefused", {
  reason: S.Literal("wrongState", "invalid"),
  message: S.String,
  details: S.Array(S.Struct({ path: S.String, message: S.String })),
}) {}
